import { ApiError } from './errors';
import type { ApiErrorBody } from './types';

export type AuthErrorKind = 'unauthorized' | 'forbidden' | 'identityProviderUnavailable';
export type AuthErrorHandler = (kind: AuthErrorKind, message: string | undefined) => void;
export type TokenProvider = () => Promise<string>;

let tokenProvider: TokenProvider | null = null;
let authErrorHandler: AuthErrorHandler | null = null;

/** Von `auth/AuthProvider.tsx` beim Start registriert -- vermeidet eine Kreisabhängigkeit client.ts <-> auth. */
export function setTokenProvider(fn: TokenProvider): void {
  tokenProvider = fn;
}

/**
 * security-design-web-oberflaeche: globaler Interceptor, der 401/403/503 VOR
 * der normalen TanStack-Query-Fehlerbehandlung an einen eigenen
 * Vollbild-Fehlerzustand weiterleitet (business-logic-model-web-oberflaeche,
 * Abschnitt "Authentifizierung — geschlossene Lücke").
 */
export function setAuthErrorHandler(fn: AuthErrorHandler): void {
  authErrorHandler = fn;
}

let baseUrl: string = import.meta.env.VITE_API_BASE_URL;

/** Von `main.tsx` nach dem Laden der Laufzeit-`config.json` gesetzt. */
export function setApiBaseUrl(url: string): void {
  baseUrl = url;
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = new URL(`${baseUrl}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

async function parseErrorBody(response: Response): Promise<ApiErrorBody | null> {
  try {
    return (await response.json()) as ApiErrorBody;
  } catch {
    return null;
  }
}

/**
 * Zentraler Fetch-Wrapper. Trägt ausschliesslich den `Authorization`-Header
 * (kein `credentials: 'include'`, SR-1/SR-2 aus security-design-web-oberflaeche
 * -- der Cookie-Pfad von rest-api bleibt ungenutzt, siehe
 * business-logic-model-web-oberflaeche "Authentifizierung"). Behandelt
 * 401/403/503 einheitlich für JSON- UND HTML-Antworten (z.B. der
 * Snapshot-Inhalt-Endpunkt, der `text/html` statt JSON liefert).
 */
async function performRequest(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (tokenProvider) {
    const token = await tokenProvider();
    headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    const init: RequestInit = { method: options.method ?? 'GET', headers };
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(options.body);
    }
    response = await fetch(buildUrl(path, options.query), init);
  } catch {
    throw new ApiError(0, null, 'Der Server antwortet gerade nicht.');
  }

  if (response.status === 401) {
    authErrorHandler?.('unauthorized', undefined);
    throw new ApiError(401, null, 'Nicht angemeldet.');
  }

  if (response.status === 403) {
    const body = await parseErrorBody(response);
    authErrorHandler?.('forbidden', body?.error.message);
    throw new ApiError(403, body, 'Kein Zugriff für dieses Konto.');
  }

  if (response.status === 503) {
    const body = await parseErrorBody(response);
    authErrorHandler?.('identityProviderUnavailable', body?.error.message);
    throw new ApiError(503, body, 'Die Anmeldung ist gerade nicht möglich.');
  }

  if (!response.ok) {
    const body = await parseErrorBody(response);
    throw new ApiError(response.status, body, 'Unerwarteter Fehler.');
  }

  return response;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await performRequest(path, options);
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * Für `GET /api/snapshots/{id}/content` (liefert rohes `text/html`, kein
 * JSON) -- genutzt vom Markierungsmodus und der Abrufhistorie (F-04), beide
 * betten den Text über `sanitizeSnapshotHtml`/ein sandboxed iframe ein,
 * niemals über eine direkte Anker-Navigation (die den Authorization-Header
 * nicht mitsenden könnte).
 */
export async function requestHtml(path: string): Promise<string> {
  const response = await performRequest(path, { method: 'GET' });
  return response.text();
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']): Promise<T> => request<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
};
